import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Package, FileText, ShoppingCart, LogOut, Settings, Menu, X } from 'lucide-react';

// Dashboard sub-pages
import DashboardHome from './dashboard/DashboardHome';
import Products from './dashboard/Products';
import CustomPages from './dashboard/CustomPages';
import Orders from './dashboard/Orders';
import ManageStore from './dashboard/ManageStore';
import NewOrderWatcher from '../components/NewOrderWatcher';

const VendorDashboard = () => {
  const { vendor, vendorLogout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isStaff = vendor?.role === 'staff';
  const STAFF_ALLOWED = ['/dashboard/products', '/dashboard/orders'];

  // Check subscription status on route change (owner only)
  React.useEffect(() => {
    const checkSubscription = async () => {
      if (!vendor || isStaff) return;

      // Allow the subscription view itself (Manage Store > Subscription)
      if (location.pathname === '/dashboard/subscription' ||
          location.pathname === '/dashboard/manage') return;

      try {
        const token = localStorage.getItem('vendor_token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vendor/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = await response.json();

        // Check if subscription is expired
        if (data.subscription?.is_expired) {
          navigate('/dashboard/manage?tab=subscription');
        }
      } catch (error) {
        console.error('Subscription check failed:', error);
      }
    };

    checkSubscription();
  }, [location.pathname, vendor, navigate, isStaff]);

  // Staff are limited to Products & Orders — redirect away from anything else.
  React.useEffect(() => {
    if (isStaff && !STAFF_ALLOWED.some((p) => location.pathname.startsWith(p))) {
      navigate('/dashboard/products', { replace: true });
    }
  }, [isStaff, location.pathname, navigate]);

  const handleLogout = () => {
    vendorLogout();
    navigate('/vendor/auth');
  };

  const ownerNav = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Overview', exact: true },
    { path: '/dashboard/products', icon: Package, label: 'Products' },
    { path: '/dashboard/orders', icon: ShoppingCart, label: 'Orders' },
    { path: '/dashboard/pages', icon: FileText, label: 'Custom Pages' },
    {
      path: '/dashboard/manage',
      icon: Settings,
      label: 'Manage Store',
      match: ['/dashboard/manage', '/dashboard/subscription', '/dashboard/store-link', '/dashboard/theme', '/dashboard/qr-code', '/dashboard/settings'],
    },
  ];
  const staffNav = [
    { path: '/dashboard/products', icon: Package, label: 'Products' },
    { path: '/dashboard/orders', icon: ShoppingCart, label: 'Orders' },
  ];
  const navItems = isStaff ? staffNav : ownerNav;

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    const paths = item.match || [item.path];
    return paths.some((p) => location.pathname.startsWith(p));
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Instant new-order alarm + popup (runs on every dashboard route) */}
      <NewOrderWatcher />

      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b bg-card sticky top-0 z-40">
        <div>
          <h1 className="text-xl font-bold" data-testid="dashboard-title">SAANJH</h1>
          <p className="text-xs text-muted-foreground">{vendor?.business_name}</p>
          <p className="text-[10px] text-muted-foreground/80">{isStaff ? `${vendor?.name} · Staff` : 'Owner'}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 h-full
          w-64 border-r bg-card z-50
          transform transition-transform duration-300 ease-in-out
          lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Desktop Header */}
          <div className="hidden lg:block p-6 border-b">
            <h1 className="text-2xl font-bold tracking-tight" data-testid="dashboard-title-desktop">SAANJH</h1>
            <p className="text-sm text-muted-foreground mt-1">{vendor?.business_name}</p>
            <p className="text-xs text-muted-foreground/80 mt-0.5">
              {isStaff ? `${vendor?.name} · Staff` : 'Owner'}
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout Button - Fixed at bottom */}
          <div className="p-4 border-t mt-auto">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 bg-background overflow-x-hidden">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="products" element={<Products />} />
            <Route path="orders" element={<Orders />} />
            <Route path="pages" element={<CustomPages />} />
            {/* Manage Store consolidates Account, Store Link, Theme, QR Code & Subscription */}
            <Route path="manage" element={<ManageStore />} />
            {/* Backward-compatible deep links open the matching tab */}
            <Route path="subscription" element={<ManageStore tab="subscription" />} />
            <Route path="store-link" element={<ManageStore tab="store-link" />} />
            <Route path="theme" element={<ManageStore tab="theme" />} />
            <Route path="qr-code" element={<ManageStore tab="qr-code" />} />
            <Route path="settings" element={<ManageStore tab="account" />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default VendorDashboard;
